import { Container, Graphics, Rectangle, Sprite, Texture } from "pixi.js";
import { BASE_ROWS } from "../gridSize";
import { CELL, FRAME_ART, art } from "./layout";

/**
 * The grid's gold frame for any number of rows (fractional while GROW grows it), built from the frame art's three
 * slices (`FRAME_ART.slices`): the top border with row 0, then row 1's slice once per row between the first and the last,
 * then the last row with the bottom border. The repeated slices are clipped at the bottom slice's top, so a row half
 * grown never shows through the last row's window.
 */
export class GridFrameView extends Container {
  private readonly bottom: Sprite;
  private readonly bandTexture: Texture;
  private readonly bands: Sprite[] = [];
  private readonly bandLayer: Container;
  private readonly bandClip: Graphics;
  /** The slices' heights in design units, from the cut the image allows (a whole pixel of the imported art). */
  private readonly topHeight: number;

  public constructor(texture: Texture) {
    super({ label: "GridFrameLayer" });
    // The imported image's pixels per delivery pixel; the cuts land on whole pixels of the image.
    const k = texture.width / FRAME_ART.width;
    const cutTop = Math.round(FRAME_ART.slices.top * k);
    const cutBottom = Math.round(FRAME_ART.slices.bottom * k);
    const slice = (y0: number, y1: number): Texture =>
      new Texture({ source: texture.source, frame: new Rectangle(texture.frame.x, texture.frame.y + y0, texture.frame.width, y1 - y0) });
    const width = art(FRAME_ART.width);
    const designHeight = (pixels: number): number => art(pixels / k);
    this.topHeight = designHeight(cutTop);
    this.x = art(-FRAME_ART.origin.x);
    this.y = art(-FRAME_ART.origin.y);

    const top = new Sprite({ texture: slice(0, cutTop), label: "Top" });
    top.setSize(width, this.topHeight);
    this.bandTexture = slice(cutTop, cutBottom);
    this.bandLayer = new Container({ label: "Rows" });
    this.bandClip = new Graphics();
    this.bandLayer.addChild(this.bandClip);
    this.bandLayer.mask = this.bandClip;
    this.bottom = new Sprite({ texture: slice(cutBottom, texture.frame.height), label: "Bottom" });
    this.bottom.setSize(width, designHeight(texture.frame.height - cutBottom));
    this.addChild(top, this.bandLayer, this.bottom);
    this.layoutRows(BASE_ROWS);
  }

  /** The frame for `rows` rows in play: row 1's slice for every row between the first and the last, the bottom after. */
  public layoutRows(rows: number): void {
    const middle = rows - 2;
    const count = Math.ceil(middle - 1e-6);
    while (this.bands.length < count) {
      const band = new Sprite({ texture: this.bandTexture, label: `Row${this.bands.length + 1}` });
      band.setSize(art(FRAME_ART.width), CELL.pitchY);
      this.bandLayer.addChild(band);
      this.bands.push(band);
    }
    this.bands.forEach((band, i) => {
      band.visible = i < count;
      band.y = this.topHeight + i * CELL.pitchY;
    });
    const bottomY = this.topHeight + middle * CELL.pitchY;
    this.bottom.y = bottomY;
    this.bandClip.clear().rect(0, this.topHeight, art(FRAME_ART.width), bottomY - this.topHeight).fill(0xffffff);
  }
}
